import { BaseType, CostUnit } from '../types/Enums';

// Centralized list of relevant BaseTypes for maintenance and component management
// This list defines the order and which BaseTypes are included across the application
// Order matches BuildingsPage button layout
export const RELEVANT_BASE_TYPES = [
    BaseType.Roof,
    BaseType.RoofSafety,
    BaseType.RoofDrainage,
    BaseType.Chimney,
    BaseType.Facade,
    BaseType.Balcony,
    BaseType.Foundation,
    BaseType.GroundDrainage,
    BaseType.Riser,
    BaseType.Window,
    BaseType.Door,
    BaseType.LockMechanism,
    BaseType.SheetMetal,
    BaseType.Utility,
    BaseType.FixedFurnishing,
    BaseType.Outdoor,
    BaseType.Miscellaneous,
    BaseType.Floor,
    BaseType.Ceiling,
    BaseType.Wall,
    BaseType.Membrane
];

// BaseTypes that are relevant for breadcrumb navigation
export const NAVIGABLE_BASE_TYPES = [
    BaseType.Roof,
    BaseType.Foundation,
    BaseType.RoofDrainage,
    BaseType.GroundDrainage,
    BaseType.Riser,
    BaseType.Facade,
    BaseType.Balcony,
    BaseType.Outdoor,
    BaseType.Miscellaneous,
    BaseType.FixedFurnishing,
    BaseType.Chimney
];

// Grouped BaseTypes for MaintenanceDraft sections
// Each group will be displayed as a single section with a combined title
export interface BaseTypeGroup {
    key: string;
    label: string;
    baseTypes: BaseType[];
}

// Helper function to generate label from baseTypes using translations
// Import translate lazily to avoid circular dependency issues
const generateBaseTypeGroupLabel = (baseTypes: BaseType[]): string => {
    // Lazy import to avoid circular dependency with EnumTranslations -> protocols -> EnumTranslations
    const { translate } = require('../types/EnumTranslations');
    
    if (baseTypes.length === 0) {
        return '';
    }
    
    if (baseTypes.length === 1) {
        return translate(baseTypes[0]);
    }
    
    // Translate all baseTypes
    const translations = baseTypes.map(baseType => translate(baseType));
    
    // Keep first capitalized, lowercase the rest
    const first = translations[0];
    const rest = translations.slice(1).map(t => t.toLowerCase());
    
    // Join all but the last with ", ", then join the last with " & "
    if (rest.length === 1) {
        return `${first} & ${rest[0]}`;
    }
    
    const allButLast = rest.slice(0, -1);
    const last = rest[rest.length - 1];
    
    return `${first}, ${allButLast.join(', ')} & ${last}`;
};

// Base type group definitions (without labels to avoid circular dependency issues)
const BASE_TYPE_GROUP_DEFINITIONS: Omit<BaseTypeGroup, 'label'>[] = [
    {
        key: 'roof_drainage',
        baseTypes: [BaseType.Roof, BaseType.RoofDrainage, BaseType.SheetMetal, BaseType.RoofSafety]
    },
    {
        key: 'facade-windows',
        baseTypes: [BaseType.Facade, BaseType.Window, BaseType.Balcony]
    },
    {
        key: 'chimney',
        baseTypes: [BaseType.Chimney]
    },
    {
        key: 'foundation',
        baseTypes: [BaseType.Foundation, BaseType.GroundDrainage]
    },
    {
        key: 'interior_surfaces',
        baseTypes: [BaseType.Floor, BaseType.Ceiling, BaseType.Wall, BaseType.Membrane]
    },
    {
        key: 'utility',
        baseTypes: [BaseType.Utility]
    },
    {
        key: 'door',
        baseTypes: [BaseType.Door, BaseType.LockMechanism]
    },
    {
        key: 'outdoor',
        baseTypes: [BaseType.Outdoor]
    },
    // Add new groups at the end: chart colours follow the group index, so a
    // group added earlier would change the colour of every group after it.
    {
        key: 'riser',
        baseTypes: [BaseType.Riser]
    },
    {
        key: 'miscellaneous',
        baseTypes: [BaseType.Miscellaneous]
    },
    {
        key: 'fixed_furnishing',
        baseTypes: [BaseType.FixedFurnishing]
    }
];

// Lazy initialization to avoid circular dependency issues
// Compute labels only when the array is first accessed
let _baseTypeGroupsCache: BaseTypeGroup[] | null = null;

function getBaseTypeGroups(): BaseTypeGroup[] {
    if (!_baseTypeGroupsCache) {
        _baseTypeGroupsCache = BASE_TYPE_GROUP_DEFINITIONS.map(group => ({
            ...group,
            label: generateBaseTypeGroupLabel(group.baseTypes)
        }));
    }
    return _baseTypeGroupsCache;
}

// Export as a Proxy that computes labels lazily when first accessed
// This defers the translation computation until after all modules are loaded
export const BASE_TYPE_GROUPS: BaseTypeGroup[] = new Proxy([] as BaseTypeGroup[], {
    get(target, prop) {
        const groups = getBaseTypeGroups();
        const value = (groups as any)[prop];
        return typeof value === 'function' ? value.bind(groups) : value;
    },
    ownKeys() {
        return Reflect.ownKeys(getBaseTypeGroups());
    },
    getOwnPropertyDescriptor(target, prop) {
        return Reflect.getOwnPropertyDescriptor(getBaseTypeGroups(), prop);
    },
    has(target, prop) {
        return Reflect.has(getBaseTypeGroups(), prop);
    },
    getPrototypeOf() {
        return Array.prototype;
    }
});

// The group's label naming only the base types that are present ("Fasad & balkonger"
// when a plan has no windows), in the group's order; the full label when none are
export const presentBaseTypeGroupLabel = (group: BaseTypeGroup, isPresent: (baseType: BaseType) => boolean): string => {
    const present = group.baseTypes.filter(isPresent);
    return present.length > 0 ? generateBaseTypeGroupLabel(present) : group.label;
};

// Helper functions
export const isRelevantBaseType = (baseType: BaseType): boolean => {
    return RELEVANT_BASE_TYPES.includes(baseType);
};

export const getBaseTypeOrder = (baseType: BaseType): number => {
    const index = RELEVANT_BASE_TYPES.indexOf(baseType);
    return index === -1 ? 999 : index; // Put unknown types at the end
};

// Cost unit preselected for a new lifetime or maintenance template.
// Stammar have no area and often no known length, and Övrigt and fast inredning
// have no area at all, so they default to a cost per component.
export const defaultCostUnitForBaseType = (baseType: BaseType | undefined): CostUnit => {
    return baseType === BaseType.Riser || baseType === BaseType.Miscellaneous || baseType === BaseType.FixedFurnishing
        || baseType === BaseType.LockMechanism
        ? 'per_unit'
        : 'per_sqm';
};

// Total löpmeter of components measured by length (stammar)
export const totalLinearMeters = (components: { linearMeters?: { value?: number | null } }[]): number => {
    return components.reduce((sum, component) => sum + (component.linearMeters?.value || 0), 0);
};
